import React, { useState, useMemo, useCallback } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { FiBarChart2 as BarChart3 } from "react-icons/fi";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import MetricCards from "../../../Components/CipAnalytics/MetricCards";
import FilterPanel from "../../../Components/CipAnalytics/FilterPanel";
import { ALERT_TYPE_MAPPING, ALERT_COLORS } from "../../../General/alertTypes";
import { formatDuration, safeNumber } from "../../../General/timeFormatter";
import { navigateToBeveragesWithFilters } from "../../../utils/cipNavigationUtils";
import { generateChartData } from "../../../utils/cipDataUtils";
import { useCipFilters } from "../../../hooks/useCipFilters";
import { useToggleHandlers } from "../../../utils/cipToggleUtils";
import {
  CHART_CONFIG,
  METRICS,
  VIEW_MODES,
} from "../../../constants/cipConstants";
import { useCipMetrics } from "../../../hooks/useCipMetrics";

export default function CipAnalysisPage({
  filteredData,
  cipTotalsData,
  totalAlerts,
  totalTimeWaste,
  totalWaterWaste,
  totalChemicalWaste,
  convertVolume,
  convertTime,
  getVolumeUnit,
  getTimeUnit,
  isCubicMeters = false,
}) {
  const history = useHistory();

  // Analysis-specific state
  const [selectedMetric, setSelectedMetric] = useState("alerts");
  const [chartType, setChartType] = useState("stacked");
  const [viewMode, setViewMode] = useState("object");
  const [showAbsolute, setShowAbsolute] = useState(true);

  // Use the custom hook for filter management
  const {
    selectedObjects,
    setSelectedObjects,
    selectedLines,
    setSelectedLines,
    selectedRecipes,
    setSelectedRecipes,
    selectedAlertTypes,
    setSelectedAlertTypes,
    searchFilters,
    uniqueObjects,
    uniqueLines,
    uniqueRecipes,
    uniqueAlertTypes,
    filteredObjects,
    filteredRecipes,
    updateSearchFilter,
    clearAllFilters,
  } = useCipFilters(filteredData);

  const getMetricUnit = useCallback(() => {
    if (selectedMetric === "cip_rate") {
      return showAbsolute ? "CIPs" : "%";
    } else if (selectedMetric === "alerts") {
      return "Alerts";
    } else if (selectedMetric === "time_waste_s") {
      return ""; // Time already includes unit in formatDuration
    } else if (
      selectedMetric === "water_waste_l" ||
      selectedMetric === "chemical_waste_l"
    ) {
      return getVolumeUnit();
    }
    return "";
  }, [selectedMetric, showAbsolute, getVolumeUnit]);

  const processedTotals = useMemo(() => {
    // Filter out Down Time and Break from totals BEFORE applying other filters
    const filteredTotalsData = cipTotalsData.filter((item) => {
      // First exclude Down Time and Break
      if (item.objectName === "Down Time" || item.objectName === "Break") {
        return false;
      }

      // Then apply user-selected filters
      const objectMatch =
        selectedObjects.length === 0 ||
        selectedObjects.includes(item.objectName);
      const lineMatch =
        selectedLines.length === 0 || selectedLines.includes(item.line);
      const recipeMatch =
        selectedRecipes.length === 0 ||
        selectedRecipes.includes(item.recipeName);

      return objectMatch && lineMatch && recipeMatch;
    });

    const groupedTotals = {
      byLine: {},
      byObject: {},
      byRecipe: {},
      byObjectAndLine: {},
      byRecipeAndLine: {},
    };

    filteredTotalsData.forEach((item) => {
      const line = item.line;
      const objectName = item.objectName;
      const recipeName = item.recipeName;

      // Count by line
      groupedTotals.byLine[line] = (groupedTotals.byLine[line] || 0) + 1;

      // Count by object
      groupedTotals.byObject[objectName] =
        (groupedTotals.byObject[objectName] || 0) + 1;

      // Count by recipe
      groupedTotals.byRecipe[recipeName] =
        (groupedTotals.byRecipe[recipeName] || 0) + 1;

      // Count by object + line combination
      const objectLineKey = `${objectName}_${line}`;
      groupedTotals.byObjectAndLine[objectLineKey] =
        (groupedTotals.byObjectAndLine[objectLineKey] || 0) + 1;

      // Count by recipe + line combination
      const recipeLineKey = `${recipeName}_${line}`;
      groupedTotals.byRecipeAndLine[recipeLineKey] =
        (groupedTotals.byRecipeAndLine[recipeLineKey] || 0) + 1;
    });

    return groupedTotals;
  }, [cipTotalsData, selectedObjects, selectedLines, selectedRecipes]);

  // Use toggle handlers hook
  const { createToggleHandler, createAlertTypeToggle } = useToggleHandlers();

  // Apply analysis filters
  const analysisFilteredData = useMemo(() => {
    return filteredData.filter((row) => {
      const objectMatch = selectedObjects.includes(row.object_name);
      const lineMatch = selectedLines.includes(row.line);
      const recipeMatch = selectedRecipes.includes(row.recipe_name);
      const alertTypeMatch = selectedAlertTypes.includes(row.alert_id);

      return objectMatch && lineMatch && recipeMatch && alertTypeMatch;
    });
  }, [
    filteredData,
    selectedObjects,
    selectedLines,
    selectedRecipes,
    selectedAlertTypes,
  ]);

  const cipMetrics = useCipMetrics(filteredData, cipTotalsData);

  // Generate chart data using utility
  const chartData = useMemo(() => {
    return generateChartData(
      analysisFilteredData,
      viewMode,
      selectedAlertTypes,
      selectedMetric,
      convertVolume,
      processedTotals,
      showAbsolute,
      cipTotalsData
    );
  }, [
    analysisFilteredData,
    selectedMetric,
    viewMode,
    selectedAlertTypes,
    convertVolume,
    processedTotals,
    showAbsolute,
    cipTotalsData,
  ]);

  // Handler functions
  const handleMultiSelect = useCallback((value, _unused, setSelection) => {
    setSelection((prev) =>
      prev.includes(value) ? prev.filter((i) => i !== value) : [...prev, value]
    );
  }, []);

  const selectedMetricInfo = useMemo(
    () => METRICS.find((m) => m.key === selectedMetric),
    [selectedMetric]
  );

  const handleBarClick = useCallback(
    (data, payload) => {
      const clickData = { alertType: payload.dataKey };

      if (viewMode === "object") {
        clickData.objectName = data.name;
      } else if (viewMode === "line") {
        clickData.line = data.name;
      } else if (viewMode === "recipe") {
        clickData.recipe = data.name;
      }

      navigateToBeveragesWithFilters(history, clickData);
    },
    [history, viewMode]
  );

  const CustomTooltip = useCallback(
    ({ active, payload, label }) => {
      if (active && payload && payload.length) {
        let total, alertedCipCount;

        if (selectedMetric === "cip_rate") {
          // For CIP rate, calculate unique defected CIPs for this group
          let uniqueAlertedCips = new Set();
          if (viewMode === "object") {
            analysisFilteredData
              .filter((row) => row.object_name === label)
              .forEach((row) => uniqueAlertedCips.add(row.cip_object_id));
          } else if (viewMode === "line") {
            analysisFilteredData
              .filter((row) => row.line === label)
              .forEach((row) => uniqueAlertedCips.add(row.cip_object_id));
          } else if (viewMode === "recipe") {
            analysisFilteredData
              .filter((row) => row.recipe_name === label)
              .forEach((row) => uniqueAlertedCips.add(row.cip_object_id));
          }

          alertedCipCount = uniqueAlertedCips.size;

          // For CIP rate, use UNFILTERED totals for accurate percentage
          let totalCips = 0;
          if (viewMode === "object") {
            const unfilteredCount = cipTotalsData.filter(
              (item) => item.objectName === label || item.object_name === label
            ).length;
            totalCips = unfilteredCount;
          } else if (viewMode === "line") {
            const unfilteredCount = cipTotalsData.filter(
              (item) => item.line === label
            ).length;
            totalCips = unfilteredCount;
          } else if (viewMode === "recipe") {
            const unfilteredCount = cipTotalsData.filter(
              (item) => item.recipeName === label || item.recipe_name === label
            ).length;
            totalCips = unfilteredCount;
          }

          if (showAbsolute) {
            total = alertedCipCount;
          } else {
            total = totalCips > 0 ? (alertedCipCount / totalCips) * 100 : 0;
          }
        } else {
          // For other metrics, sum up the values (existing logic)
          total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);

          // Calculate alertedCipCount for other metrics
          let totalCips = 0;
          if (viewMode === "object") {
            totalCips = processedTotals?.byObject?.[label] || 0;
          } else if (viewMode === "line") {
            totalCips = processedTotals?.byLine?.[label] || 0;
          } else if (viewMode === "recipe") {
            totalCips = processedTotals?.byRecipe?.[label] || 0;
          }

          if (viewMode === "object") {
            const uniqueCips = new Set(
              analysisFilteredData
                .filter((row) => row.object_name === label)
                .map((row) => row.cip_object_id)
            );
            alertedCipCount = uniqueCips.size;
          } else if (viewMode === "line") {
            const uniqueCips = new Set(
              analysisFilteredData
                .filter((row) => row.line === label)
                .map((row) => row.cip_object_id)
            );
            alertedCipCount = uniqueCips.size;
          } else if (viewMode === "recipe") {
            const uniqueCips = new Set(
              analysisFilteredData
                .filter((row) => row.recipe_name === label)
                .map((row) => row.cip_object_id)
            );
            alertedCipCount = uniqueCips.size;
          }
        }

        // Use the proper totalCips for percentage calculation in Alert Rate display
        let displayTotalCips = 0;
        if (viewMode === "object") {
          displayTotalCips =
            selectedMetric === "cip_rate"
              ? cipTotalsData.filter(
                  (item) =>
                    item.objectName === label || item.object_name === label
                ).length
              : processedTotals?.byObject?.[label] || 0;
        } else if (viewMode === "line") {
          displayTotalCips =
            selectedMetric === "cip_rate"
              ? cipTotalsData.filter((item) => item.line === label).length
              : processedTotals?.byLine?.[label] || 0;
        } else if (viewMode === "recipe") {
          displayTotalCips =
            selectedMetric === "cip_rate"
              ? cipTotalsData.filter(
                  (item) =>
                    item.recipeName === label || item.recipe_name === label
                ).length
              : processedTotals?.byRecipe?.[label] || 0;
        }

        const percentage =
          displayTotalCips > 0
            ? ((alertedCipCount / displayTotalCips) * 100).toFixed(1)
            : "0.0";

        let formattedTotal;
        let unit;

        if (selectedMetric === "cip_rate") {
          if (showAbsolute) {
            formattedTotal = safeNumber(total, 0).toLocaleString();
          } else {
            formattedTotal = total.toFixed(1);
          }
        } else if (
          selectedMetric === "water_waste_l" ||
          selectedMetric === "chemical_waste_l"
        ) {
          if (isCubicMeters) {
            formattedTotal = total.toLocaleString(undefined, {
              minimumFractionDigits: 3,
              maximumFractionDigits: 3,
            });
          } else {
            formattedTotal = total.toLocaleString(undefined, {
              minimumFractionDigits: total % 1 === 0 ? 0 : 1,
              maximumFractionDigits: total % 1 === 0 ? 0 : 1,
            });
          }
        } else if (selectedMetric === "time_waste_s") {
          formattedTotal = formatDuration(total);
        } else {
          formattedTotal = safeNumber(total, 0).toLocaleString();
        }

        // Get unit using the generic function
        unit = getMetricUnit();

        return (
          <div
            style={{
              backgroundColor: "white",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
              padding: "12px",
              textAlign: "left",
            }}
          >
            <div
              style={{
                fontWeight: "600",
                marginBottom: "8px",
                fontSize: "14px",
              }}
            >
              {viewMode.charAt(0).toUpperCase() + viewMode.slice(1)}: {label}
            </div>

            <div
              style={{
                marginBottom: "8px",
                fontWeight: "600",
                color: "#1f2937",
                borderBottom: "1px solid #e5e7eb",
                paddingBottom: "6px",
              }}
            >
              Total: {formattedTotal} {unit}
            </div>

            {displayTotalCips > 0 && (
              <div
                style={{
                  marginBottom: "8px",
                  fontSize: "13px",
                  color: "#6b7280",
                  borderBottom: "1px solid #e5e7eb",
                  paddingBottom: "6px",
                }}
              >
                Alert Rate: {alertedCipCount} of {displayTotalCips} CIPs (
                {percentage}%)
              </div>
            )}

            {payload.map((entry, index) => {
              let formattedValue;
              if (selectedMetric === "cip_rate") {
                if (showAbsolute) {
                  formattedValue = safeNumber(entry.value, 0).toLocaleString();
                } else {
                  formattedValue = entry.value.toFixed(1) + "%";
                }
              } else if (
                selectedMetric === "water_waste_l" ||
                selectedMetric === "chemical_waste_l"
              ) {
                if (isCubicMeters) {
                  formattedValue = entry.value.toLocaleString(undefined, {
                    minimumFractionDigits: 3,
                    maximumFractionDigits: 3,
                  });
                } else {
                  formattedValue = entry.value.toLocaleString(undefined, {
                    minimumFractionDigits: entry.value % 1 === 0 ? 0 : 1,
                    maximumFractionDigits: entry.value % 1 === 0 ? 0 : 1,
                  });
                }
              } else if (selectedMetric === "time_waste_s") {
                formattedValue = formatDuration(entry.value);
              } else {
                formattedValue = safeNumber(entry.value, 0).toLocaleString();
              }

              const alertTypeName =
                ALERT_TYPE_MAPPING[entry.dataKey] || `Alert ${entry.dataKey}`;

              return (
                <div
                  key={index}
                  style={{
                    marginBottom: "4px",
                    display: "flex",
                    alignItems: "center",
                    fontSize: "13px",
                  }}
                >
                  <div
                    style={{
                      width: "12px",
                      height: "12px",
                      backgroundColor: entry.color,
                      marginRight: "8px",
                      borderRadius: "2px",
                    }}
                  />
                  <span style={{ color: "#6b7280" }}>{alertTypeName}:</span>
                  <span style={{ marginLeft: "6px", fontWeight: "500" }}>
                    {formattedValue}{" "}
                    {selectedMetric !== "time_waste_s" ? unit : ""}
                  </span>
                </div>
              );
            })}

            <div
              style={{
                marginTop: "8px",
                fontSize: "12px",
                color: "#9ca3af",
                fontStyle: "italic",
              }}
            >
              Click to drill down
            </div>
          </div>
        );
      }

      return null;
    },
    [
      selectedMetric,
      isCubicMeters,
      getVolumeUnit,
      getMetricUnit,
      viewMode,
      processedTotals,
      analysisFilteredData,
      showAbsolute,
      cipTotalsData,
    ]
  );

  // Active filter count
  const activeFilterCount = useMemo(() => {
    const filters = [
      { selected: selectedObjects, total: uniqueObjects },
      { selected: selectedLines, total: uniqueLines },
      { selected: selectedRecipes, total: uniqueRecipes },
      { selected: selectedAlertTypes, total: uniqueAlertTypes },
    ];

    return filters.filter(
      ({ selected, total }) =>
        selected.length > 0 && selected.length !== total.length
    ).length;
  }, [
    selectedObjects.length,
    uniqueObjects.length,
    selectedLines.length,
    uniqueLines.length,
    selectedRecipes.length,
    uniqueRecipes.length,
    selectedAlertTypes.length,
    uniqueAlertTypes.length,
  ]);

  // Toggle handlers
  const handleLinesToggle = useMemo(
    () => createToggleHandler(selectedLines, setSelectedLines, uniqueLines),
    [selectedLines, setSelectedLines, uniqueLines, createToggleHandler]
  );

  const handleRecipesToggle = useMemo(
    () =>
      createToggleHandler(selectedRecipes, setSelectedRecipes, uniqueRecipes),
    [selectedRecipes, setSelectedRecipes, uniqueRecipes, createToggleHandler]
  );

  const handleObjectsToggle = useMemo(
    () =>
      createToggleHandler(selectedObjects, setSelectedObjects, uniqueObjects),
    [selectedObjects, setSelectedObjects, uniqueObjects, createToggleHandler]
  );

  const handleAlertTypeToggle = useMemo(
    () =>
      createAlertTypeToggle(
        selectedAlertTypes,
        setSelectedAlertTypes,
        uniqueAlertTypes
      ),
    [
      selectedAlertTypes,
      setSelectedAlertTypes,
      uniqueAlertTypes,
      createAlertTypeToggle,
    ]
  );

  const getYAxisLabel = useCallback(() => {
    if (selectedMetric === "cip_rate") {
      return showAbsolute ? "CIPs" : "CIPs (%)";
    } else if (selectedMetric === "alerts") {
      return "Alerts";
    } else if (selectedMetric === "time_waste_s") {
      return "Excessive Time";
    } else if (selectedMetric === "water_waste_l") {
      return "Water Loss";
    } else if (selectedMetric === "chemical_waste_l") {
      return "Chemical Loss";
    }
    return "";
  }, [selectedMetric, showAbsolute]);

  return (
    <Container fluid>
      {/* Metric Cards with Selection */}
      <MetricCards
        totalAlerts={totalAlerts}
        totalTimeWaste={totalTimeWaste}
        totalWaterWaste={totalWaterWaste}
        totalChemicalWaste={totalChemicalWaste}
        cipMetrics={cipMetrics}
        convertTime={convertTime}
        convertVolume={convertVolume}
        getVolumeUnit={getVolumeUnit}
        getTimeUnit={getTimeUnit}
        selectedMetric={selectedMetric}
        onMetricSelect={setSelectedMetric}
        showSelection={true}
        isCubicMeters={isCubicMeters}
      />

      <Row>
        {/* Filter Panel */}
        <Col lg={3}>
          <FilterPanel
            selectedObjects={selectedObjects}
            selectedLines={selectedLines}
            selectedRecipes={selectedRecipes}
            selectedAlertTypes={selectedAlertTypes}
            uniqueObjects={uniqueObjects}
            uniqueLines={uniqueLines}
            uniqueRecipes={uniqueRecipes}
            uniqueAlertTypes={uniqueAlertTypes}
            filteredObjects={filteredObjects}
            filteredRecipes={filteredRecipes}
            searchFilters={searchFilters}
            updateSearchFilter={updateSearchFilter}
            handleObjectsToggle={handleObjectsToggle}
            handleLinesToggle={handleLinesToggle}
            handleRecipesToggle={handleRecipesToggle}
            handleAlertTypeToggle={handleAlertTypeToggle}
            handleMultiSelect={handleMultiSelect}
            clearAllFilters={clearAllFilters}
            activeFilterCount={activeFilterCount}
            setSelectedAlertTypes={setSelectedAlertTypes}
            setSelectedLines={setSelectedLines}
            setSelectedRecipes={setSelectedRecipes}
            setSelectedObjects={setSelectedObjects}
          />
        </Col>
        {/* Main Chart */}
        <Col lg={9}>
          <Card className="border-0">
            <Card.Body className="p-4">
              <div className="d-flex align-items-center justify-content-between mb-4">
                <div>
                  <div className="btn-group btn-group-sm" role="group">
                    {VIEW_MODES.map((mode) => (
                      <Button
                        key={mode.key}
                        variant={
                          viewMode === mode.key
                            ? "primary"
                            : "outline-secondary"
                        }
                        size="sm"
                        onClick={() => setViewMode(mode.key)}
                      >
                        {mode.label}
                      </Button>
                    ))}
                  </div>

                  {selectedMetric === "cip_rate" && (
                    <div className="btn-group btn-group-sm ml-3" role="group">
                      <Button
                        variant={showAbsolute ? "primary" : "outline-secondary"}
                        size="sm"
                        onClick={() => setShowAbsolute(true)}
                      >
                        Absolute
                      </Button>
                      <Button
                        variant={
                          !showAbsolute ? "primary" : "outline-secondary"
                        }
                        size="sm"
                        onClick={() => setShowAbsolute(false)}
                      >
                        Percentage
                      </Button>
                    </div>
                  )}
                </div>

                <div className="text-center">
                  <h5 className="mb-0 font-weight-semibold">
                    {selectedMetricInfo?.label} Analysis
                  </h5>
                </div>

                <div className="d-flex align-items-center">
                  <span className="text-muted mr-3">
                    {(() => {
                      if (selectedMetric === "cip_rate") {
                        // For CIP metric, show unique CIPs count
                        const uniqueCipsCount = new Set(
                          analysisFilteredData.map((row) => row.cip_object_id)
                        ).size;
                        return `${uniqueCipsCount.toLocaleString()} CIPs`;
                      } else {
                        return `${analysisFilteredData.length.toLocaleString()} records`;
                      }
                    })()}
                  </span>
                  <select
                    value={chartType}
                    onChange={(e) => setChartType(e.target.value)}
                    className="form-control form-control-sm"
                    style={{ width: "auto" }}
                  >
                    <option value="stacked">Stacked View</option>
                    <option value="grouped">Grouped View</option>
                  </select>
                </div>
              </div>

              {chartData.length === 0 ? (
                <div className="text-center py-5">
                  <BarChart3 size={48} className="text-muted mb-3" />
                  <h6 className="text-muted mb-1">No data available</h6>
                  <p className="text-muted small">
                    Try adjusting your filters or time range
                  </p>
                </div>
              ) : (
                <div style={{ height: CHART_CONFIG.HEIGHT }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={CHART_CONFIG.MARGIN}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis
                        dataKey="name"
                        angle={-45}
                        textAnchor="end"
                        height={100}
                        fontSize={CHART_CONFIG.AXIS.fontSize}
                        interval={0}
                        stroke={CHART_CONFIG.AXIS.stroke}
                      />
                      <YAxis
                        stroke={CHART_CONFIG.AXIS.stroke}
                        fontSize={CHART_CONFIG.AXIS.fontSize}
                        domain={["auto", "auto"]}
                        tickFormatter={
                          selectedMetric === "cip_rate" && !showAbsolute
                            ? (value) => `${Math.round(value)}%`
                            : selectedMetric === "cip_rate" && showAbsolute
                            ? (value) => `${Math.round(value)}`
                            : undefined
                        }
                        label={{
                          value: getYAxisLabel(),
                          angle: -90,
                          position: "insideLeft",
                          style: { textAnchor: "middle" },
                        }}
                      />
                      <Tooltip content={CustomTooltip} />

                      {selectedAlertTypes.map((alertType) => (
                        <Bar
                          key={alertType}
                          dataKey={alertType}
                          fill={ALERT_COLORS[alertType] || "#64748b"}
                          name={ALERT_TYPE_MAPPING[alertType] || `${alertType}`}
                          stackId={chartType === "stacked" ? "a" : undefined}
                          radius={[2, 2, 0, 0]}
                          onClick={(data, index) =>
                            handleBarClick(data, { dataKey: alertType })
                          }
                          style={{ cursor: "pointer" }}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
